/*
 * Copyright (C) 2021 Hal Perkins.  All rights reserved.  Permission is
 * hereby granted to students registered for University of Washington
 * CSE 331 for use solely during Winter Quarter 2021 for purposes of
 * the course.  No other use, copying, distribution, or modification
 * is permitted without prior written consent. Copyrights for
 * third-party components of this work must be honored.  Instructors
 * interested in reusing these course materials should contact the
 * author.
 */

import React, {Component} from 'react';
import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch";
import "./Map.css";

import Path from "./util/Path";
import Segment from "./util/Segment";
import GeoTransform from "./util/GeoTransform";
import Point from "./util/Point";

interface MapProps {
    path: Path;             // the path to be displayed.
    latitude: number;       // the latitude of the user, if location services enabled.
    longitude: number;      // the longitude of the user, if location services enabled.
    stepByStep: boolean;    // indicates step-by-step mode is enabled.
    step: number;           // the current step to display to, if step-by-step mode is enabled.
    panAndZoom: boolean; // indicates pan and zoom is enabled.
}

interface MapState {
    backgroundImage: HTMLImageElement | null;
}

class Map extends Component<MapProps, MapState> {

    canvas: React.RefObject<HTMLCanvasElement>;
    setTransform: any;
    resetTransform: any;

    constructor(props: any) {
        super(props);
        this.state = {
            backgroundImage: null
        };
        this.canvas = React.createRef();
    }

    /**
     * Loads the background image on mounting.
     */
    componentDidMount() {
        this.fetchAndSaveImage();
    }

    /**
     * Redraws the component on update.
     */
    componentDidUpdate() {
        this.redraw();
    }

    /**
     * Redraws the map component:
     *  1: Draws the map
     *  2: Draws the campus path, if it exists
     *  3: Draws the user's location, if it exists
     *  4: Zooms and pans, if user specified
     */
    redraw = () => {
        const canvas = this.canvas.current;

        if (canvas === null) {
            throw new Error("unable to access canvas");
        }

        const ctx = canvas.getContext('2d');
        if (ctx === null) {
            throw new Error("Unable to create canvas drawing context.");
        }

        // set up edge extremes for zoom rectangle
        let left = canvas.width;
        let right = 0;
        let top = canvas.height;
        let bottom = 0;


        // Step 1a: clear everything
        ctx.clearRect(0, 0, canvas.width, canvas.height);

        // Step 1b: redraw the map
        if (this.state.backgroundImage !== null) {
            this.drawBackgroundImage();
        }

        // Step 2: draw the path
        const path = this.props.path;

        ctx.strokeStyle = "red";
        ctx.lineWidth = 5;

        for (let i = 0; i < (this.props.stepByStep ? this.props.step : path.path.length); i++) {
            let segment: Segment = path.path[i];
            ctx.beginPath();
            ctx.moveTo(segment.start.x, segment.start.y);
            ctx.lineTo(segment.end.x, segment.end.y);
            ctx.stroke();
            left = Math.min(left, Math.min(segment.start.x, segment.end.x));
            right = Math.max(right, Math.max(segment.start.x, segment.end.x));
            top = Math.min(top, Math.min(segment.start.y, segment.end.y));
            bottom = Math.max(bottom, Math.max(segment.start.y, segment.end.y));
        }

        if (path.path.length > 2) {
            const start: Point = path.start;
            const end: Point = path.path[path.path.length - 1].end;

            this.drawText(ctx, "Start", [start.x, start.y], 50, "red");
            this.drawText(ctx, "End", [end.x, end.y], 50, "red");
        }

        // Step 3: draw location, if it is valid
        const x = GeoTransform.longitudeToPixelCoordinate(this.props.longitude);
        const y = GeoTransform.latitudeToPixelCoordinate(this.props.latitude);
        if (x >= 0 && x <= canvas.width && y >= 0 && y <= canvas.height) {
            if (path.path.length >= 1) {
                ctx.strokeStyle = "cyan";
                ctx.beginPath();
                ctx.moveTo(x, y);
                ctx.lineTo(path.start.x, path.start.y);
                ctx.stroke();
                left = Math.min(left, x);
                right = Math.max(right, x);
                top = Math.min(top, y);
                bottom = Math.max(bottom, y);
            }
            this.drawCircle(ctx, [x, y], 20, "cyan");
            this.drawText(ctx, "Your location",[x, y], 100, "cyan");
        }

        // Step 4: zoom and pan
        if (!this.props.panAndZoom) return;

        let leftEdge = left;
        let topEdge = top;
        const zoomWidth = right - left;
        const widthPercent = zoomWidth / canvas.width;
        const zoomHeight = bottom - top;
        const heightPercent = zoomHeight / canvas.height;
        const scale = 1 / Math.max(widthPercent, heightPercent);

        if (zoomWidth < 0 || zoomHeight < 0) return;

        if (widthPercent > heightPercent) {
            // fix height
            const centerY = (top + bottom) / 2;
            const distFromCenter = zoomHeight / 2;
            topEdge = centerY - (distFromCenter * (widthPercent / heightPercent));
        } else {
            // fix width
            const centerX = (right + left) / 2;
            const distFromCenter = zoomWidth / 2;
            leftEdge = centerX - (distFromCenter * (heightPercent / widthPercent));
        }

        // canvas takes up 50% of the pixels
        const pixelScale = window.innerWidth * 0.5 / canvas.width;
        leftEdge *= -pixelScale;
        topEdge *= -pixelScale;


        this.setTransform((leftEdge + 4.5) * scale,
            (topEdge + 4.5) * scale,
            scale)
    }

    /**
     * Loads a new background image into the map's state,
     * and assigns the campus map as its image source.
     */
    fetchAndSaveImage() {
        // Creates an Image object, and sets a callback function
        // for when the image is done loading (it might take a while).
        let background: HTMLImageElement = new Image();
        background.onload = () => {
            this.setState({
                backgroundImage: background
            });
        };
        // Once our callback is set up, we tell the image what file it should
        // load from. This also triggers the loading process.
        background.src = "./campus_map.jpg";
    }

    /**
     * Draws a circle at a given coordinate point.
     *
     * @param ctx the 2-dimensional canvas context to be drawn on
     * @param coordinate the x, y point where the circle will be drawn
     * @param radius the radius of the circle, defaulted to 10 pixels
     * @param color the color of the circle, defaulted to red
     */
    drawCircle = (ctx: CanvasRenderingContext2D, coordinate: [number, number],
                  radius: number = 10,
                  color: string = "red") => {
        ctx.fillStyle = color;
        // Generally use a radius of 4, but when there are lots of dots on the grid (> 50)
        // we slowly scale the radius down so they'll all fit next to each other.
        ctx.beginPath();
        ctx.arc(coordinate[0], coordinate[1], radius, 0, 2 * Math.PI);
        ctx.fill();
    };

    /**
     * Draws text at a given coordinate point.
     *
     * @param ctx the 2-dimensional canvas context to be drawn on
     * @param text the text to be drawn
     * @param coordinate the x, y point where the circle will be drawn
     * @param fontSize the font size of the text, defaulted to 100 pixels
     * @param color the color of the text, defaulted to red
     */
    drawText = (ctx: CanvasRenderingContext2D, text: string,
                coordinate: [number, number],
                fontSize: number = 100,
                color: string = "red") => {
        ctx.fillStyle = color;
        ctx.font = fontSize + "px -apple-system, Roboto, Segoe UI";
        ctx.fillText(text, coordinate[0] + fontSize / 5, coordinate[1]);
    }

    /**
     * Draws the background image in the map's state.
     */
    drawBackgroundImage() {
        let canvas = this.canvas.current;
        if (canvas === null) throw Error("Unable to draw, no canvas ref.");
        let ctx = canvas.getContext("2d");
        if (ctx === null) throw Error("Unable to draw, no valid graphics context.");
        //
        if (this.state.backgroundImage !== null) { // This means the image has been loaded.
            // Sets the internal "drawing space" of the canvas to have the correct size.
            // This helps the canvas not be blurry.
            canvas.width = this.state.backgroundImage.width;
            canvas.height = this.state.backgroundImage.height;
            ctx.drawImage(this.state.backgroundImage, 0, 0);
        }
    }

    render() {
        return (
            <TransformWrapper>
                {({ setTransform, resetTransform }) => (
                    <React.Fragment>
                        {this.setTransform = setTransform}
                        {this.resetTransform = resetTransform}
                        <div className={"transform"}>
                            <TransformComponent>
                                <canvas ref={this.canvas}/>
                            </TransformComponent>
                        </div>
                    </React.Fragment>
                )}
            </TransformWrapper>
        )
    }
}

export default Map;